import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  InjectionToken,
  Input,
  input,
} from '@angular/core';

import type { ThemeName } from '@core/theme-util';
import { IconComponent, IconHelpComponent, RtTooltip } from '@core/user-interface-legacy';

export interface ThemeCardData {
  title: string;
  subtitle?: string;
  theme: ThemeName;
  hasIconHelp?: boolean;
  iconHelpTooltip?: string;
}

type ThemeLayoutsAssetsProvider = (name: 'light' | 'dark' | 'system') => Promise<string>;

const THEME_LAYOUTS_ASSETS_PROVIDER = new InjectionToken<ThemeLayoutsAssetsProvider>(
  'THEME_LAYOUTS_ASSETS_PROVIDER',
);

export const provideLayoutAssetsResolver = (provider: ThemeLayoutsAssetsProvider) => ({
  provide: THEME_LAYOUTS_ASSETS_PROVIDER,
  useValue: provider,
});

@Component({
  selector: 'rt-theme-card',
  templateUrl: './theme-card.component.html',
  styleUrls: ['./theme-card.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconHelpComponent, IconComponent, RtTooltip, AsyncPipe],
  host: {
    '[class.active]': 'isActive()',
  },
})
export class ThemeCardComponent {
  public readonly title = input<string>(undefined!);
  public readonly theme = input<ThemeName>(undefined!);
  public readonly isActive = input<boolean>(undefined!);
  public readonly width = input<number>();
  public readonly height = input<number>();
  public readonly backgroundSize = input<'auto' | 'contain'>(undefined!);
  @Input() public subtitle: string | undefined;
  @Input() public iconHelpTooltip: string | undefined;

  public assetsProvider = inject(THEME_LAYOUTS_ASSETS_PROVIDER);

  public imgSrcMap = {
    light: this.resolveBgImage('light'),
    dark: this.resolveBgImage('dark'),
    auto: this.resolveBgImage('system'),
  };

  private resolveBgImage(name: Parameters<ThemeLayoutsAssetsProvider>[0]) {
    return this.assetsProvider(name).then(path => `url(${path})`);
  }
}
